/**
 * 系統 › 帳號邀請：admin 產生一次性註冊連結（VoidMatrix AdminInvitePanel）。
 */
import { useMemo } from 'react';
import { AdminInvitePanel } from '@tpte/auth-ui';
import { AUTH_SERVICE_URL } from '../../auth/config';

async function authJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${AUTH_SERVICE_URL}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      'X-VM-Request': '1',
      ...(init?.headers ?? {}),
    },
  });
  const body = (await response.json().catch(() => ({}))) as {
    error?: { message?: string };
  } & T;
  if (!response.ok) {
    throw new Error(body.error?.message ?? `${response.status} ${path}`);
  }
  return body as T;
}

export function InvitesPage() {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://chat-rea-console.pages.dev';

  const api = useMemo(
    () => ({
      buildInviteUrl: (token: string) => `${origin}/#/invite/${encodeURIComponent(token)}`,
      createInvite: (input: {
        role: 'admin' | 'engineer' | 'chatter';
        displayName: string;
        expiresInSec: number;
        emailHint?: string;
      }) =>
        authJson<{ id: string; token: string; expiresAt: string; path: string }>('/admin/invites', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      listInvites: async () => {
        const data = await authJson<{
          invites: Array<{
            id: string;
            displayName: string;
            role: string;
            expiresAt: string;
            status: string;
            createdAt: string;
          }>;
        }>('/admin/invites');
        return data.invites;
      },
      revokeInvite: async (id: string) => {
        await authJson(`/admin/invites/${encodeURIComponent(id)}/revoke`, { method: 'POST', body: '{}' });
      },
    }),
    [origin],
  );

  return (
    <div className="p-3" data-testid="invites-page">
      <AdminInvitePanel
        buildInviteUrl={api.buildInviteUrl}
        createInvite={api.createInvite}
        listInvites={api.listInvites}
        revokeInvite={api.revokeInvite}
      />
    </div>
  );
}
